import { Component, inject, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { ShareWppDialogComponent } from "../share-wpp-dialog/share-wpp-dialog.component";
import { AddCompanyContactController } from "@arch/presentation/controllers/add-company-contact.controller";
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule
} from "@angular/forms";
import { CompanyContactEntity, ReportEntity } from "@arch/domain/entity";
import { EventsService } from "@arch/domain/application/services/events.service";
import { UpdateUserController } from "@arch/presentation/controllers/update-user.controller";
import { FormatUtils } from "@arch/_shared/utils/format.utils";
import { DDI_OPTIONS } from "@app/shared/constants/ddi.constants";

@Component({
    selector: "app-add-contact-dialog",
    imports: [SharedMaterialModule, SharedDsnModule, ReactiveFormsModule],
    templateUrl: "./add-contact-dialog.component.html",
    styleUrl: "./add-contact-dialog.component.scss"
})
export class AddContactDialogComponent implements OnInit {
    private readonly addCompanyContactController = inject(
        AddCompanyContactController
    );

    public defaultValue: string = "55";
    public prefixes = DDI_OPTIONS;
    public isInvalidWhatsapp: boolean = true;
    public contactForm: string;
    public loading: boolean = false;
    public form: FormGroup;
    public currentDdi: {
        label: string;
        value: string;
        mask: string;
        country: string;
    };
    private readonly eventsService = inject(EventsService);
    private readonly updateUserController = inject(UpdateUserController);
    constructor(
        public dialogRef: MatDialogRef<ShareWppDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: DialogInputType,
        private fb: FormBuilder
    ) {
        this.form = this.fb.group({
            username: ["", Validators.required],
            email: ["", [Validators.required, Validators.email]],
            contact: [""]
        });
    }

    ngOnInit(): void {
        this.currentDdi = this.prefixes[0];
        if (this.data.contact) {
            const contact = this.data.contact.getPhoneData();
            const contactIndex = this.prefixes.findIndex(
                (p) => p.value == contact.ddi
            );
            this.currentDdi = this.prefixes?.[contactIndex] || this.prefixes[0];
            this.defaultValue = this.currentDdi.value;
            this.form.setValue(this.data.contact.getContactForm());
        }
    }

    public onChangeDDI(ddi: string): void {
        this.currentDdi = this.prefixes.find(
            (item: {
                label: string;
                value: string;
                mask: string;
                country: string;
            }) => item.value === ddi
        );
    }

    public handleValidWhatsapp(event: any): void {
        const isValidContact = FormatUtils.mask("phone", event, {
            ddi: this.currentDdi.value
        }).valid;
        this.isInvalidWhatsapp = !isValidContact;
    }

    public async onSubmit(): Promise<void> {
        this.eventsService.send(
            `Dash.${this.data?.report?.props?.type}.${this.data?.report?.humanSourceName}.criar-contato`
        );
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.loading = true;

        const whatsapp =
            this.form.value?.contact &&
            `${this.currentDdi.value}:${this.form.value?.contact.replace(/\D/g, "")}`;

        if (this.data.contact?.isUser) {
            await this.updateUserController.execute({
                accountingId: this.data.accountingId,
                _id: this.data.contact.props._id,
                username: this.form.value.username,
                email: this.form.value.email,
                ...(whatsapp && {
                    whatsapp
                })
            });
        } else {
            await this.addCompanyContactController.execute({
                accountingId: this.data.accountingId,
                type: this.data.category,
                contact: {
                    ...(this.data?.contact?.props?._id && {
                        _id: this.data?.contact?.props?._id
                    }),
                    email: this.form.value.email,
                    name: this.form.value.username,
                    ...(whatsapp && {
                        whatsapp
                    }),
                    relations: {
                        nps: {
                            status: 0
                        },
                        user: {
                            status: 0
                        }
                    },
                    type: "company",
                    accountingId: this.data.accountingId,
                    companyId: this.data.companyId
                }
            });
        }
        await this.data.handleListContacts();

        this.dialogRef.close();
        this.loading = false;
    }
}

type DialogInputType = {
    title: string;
    description: string;
    companyId: string;
    accountingId: string;
    type: "whatsapp" | "email";
    category: "company" | "accounting";
    handleListContacts: () => Promise<void>;
    action?: string;
    contact?: CompanyContactEntity;
    report: ReportEntity;
};
